import { useEditorContext } from './EditorProvider'

/**
 * Main editor hook - provides access to all editor functionality
 * Use this when you need full editor context access
 */
export const useEditor = () => {
	const context = useEditorContext()
	return context
}

/**
 * Editor content hook - focused on content management and updates
 * Use this in components that need to display and modify editor content
 */
export const useEditorContent = () => {
	const {
		content,
		updateWysiwyg,
		updateHtml,
		updateCss,
		updateContent,
		setContent,
		isDirty,
		errors,
		validateContent,
		clearErrors,
		editorConfig,
	} = useEditorContext()

	return {
		content,
		updateWysiwyg,
		updateHtml,
		updateCss,
		updateContent,
		setContent,
		isDirty,
		errors,
		validateContent,
		clearErrors,
		editorConfig,
	}
}

/**
 * Editor tabs hook - focused on tab management and navigation
 * Use this in components that need to control or display editor tabs
 */
export const useEditorTabs = () => {
	const { activeTab, switchTab, getTabContent, editorConfig } = useEditorContext()

	return {
		activeTab,
		switchTab,
		getTabContent,
		editorConfig,
	}
}

/**
 * Editor state hook - focused on editor state management
 * Use this in components that need to display editor status or manage state
 */
export const useEditorState = () => {
	const { isDirty, markDirty, markClean, resetContent, errors, validateContent } = useEditorContext()

	// Computed state values
	const hasErrors = errors.length > 0
	const hasContent = (content: { wysiwyg: string; html: string; css: string }) =>
		content.wysiwyg.trim() !== '' || content.html.trim() !== '' || content.css.trim() !== ''

	return {
		isDirty,
		markDirty,
		markClean,
		resetContent,
		errors,
		validateContent,
		hasErrors,
		hasContent,
	}
}

/**
 * Editor configuration hook - focused on editor configuration and settings
 * Use this in components that need access to editor configuration
 */
export const useEditorConfig = () => {
	const { editorConfig } = useEditorContext()

	return {
		editorConfig,
		wysiwygConfig: editorConfig.wysiwyg,
		htmlConfig: editorConfig.html,
		cssConfig: editorConfig.css,
	}
}
